/* eslint-disable @typescript-eslint/no-explicit-any */
import { Edit3 } from 'lucide-react';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { toast } from 'sonner';
import { formatColumnName } from './ImportMapping/ImportedData';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import useCreateMortgageCompany from '@/hooks/useCreateMortgageCompany';
import PhoneInput from './ui/phoneInput';
import AvatarUploader from './AvatarUploader';

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  mortgageCompany?: any;
}

const getEditableFields = () => {
  return ['name', 'emailAddress', 'website', 'mlsLicense'];
};

const MortgageCompanyFormModal: FC<Props> = ({
  isOpen,
  setIsOpen,
  mortgageCompany,
}) => {
  const { mutate: createMortgageCompany, isPending: createPending } =
    useCreateMortgageCompany();
  const [editingRow, setEditingRow] = useState({
    name: mortgageCompany?.name || '',
    emailAddress: mortgageCompany?.emailAddress || '',
    phoneNumber: mortgageCompany?.phoneNumber || '',
    imageUrl: mortgageCompany?.imageUrl || '',
  });

  const handleFieldChange = (field: string, value: string) => {
    if (editingRow) {
      setEditingRow((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleCreateMortgageCompany = () => {
    createMortgageCompany(editingRow, {
      onSuccess: () => {
        toast.success('Successfully Created Mortgage Company');
        setIsOpen(false);
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            Edit Record
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <AvatarUploader
                profileImage={editingRow?.imageUrl || '/placeholder.svg'}
                onSuccess={(data) => handleFieldChange('imageUrl', data.url)}
              />
            </div>
            {getEditableFields().map((field) => (
              <div key={field} className="space-y-2">
                <Label htmlFor={field} className="text-sm font-medium">
                  {formatColumnName(field)}
                </Label>
                <Input
                  id={field}
                  value={editingRow[field] || ''}
                  onChange={(e) => handleFieldChange(field, e.target.value)}
                  placeholder={`Enter ${formatColumnName(field).toLowerCase()}`}
                />
              </div>
            ))}
            <div className="space-y-2">
              <Label htmlFor="mortgage" className="text-sm font-medium">
                {formatColumnName('mortgage Company')}
              </Label>
              <PhoneInput
                value={editingRow.phoneNumber || ''}
                name="Phone Input"
                onChange={(name, val) => handleFieldChange('phoneNumber', val)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            disabled={createPending}
            variant="outline"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateMortgageCompany}
            disabled={createPending}
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MortgageCompanyFormModal;
